import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Button, Input, Select, Row, Col, Tag, message, Modal, Tooltip, Switch } from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  SettingOutlined,
  DownOutlined,
  CopyOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import usePrivateAxios from '../hooks/usePrivateAxios';
import GlobalColors from '../assets/colors/GlobalColors';
import LoadingIcon from './LoadingIcon';
import StatusNotification from './StatusNotification';
import styles from '../assets/styles/EmailSegmentationRules.module.css';
import formatQuery from '../utils/formatQuery';

const { Option } = Select;

// Field options for MongoDB segmentation queries
//TODO: maintain the db
const FIELD_OPTIONS = [
  { label: 'Email', value: 'email', type: 'email' },
  { label: 'First Name', value: 'str_first_name', type: 'str' },
  { label: 'Surname', value: 'str_surname', type: 'str' },
  { label: 'Age', value: 'int_age', type: 'int' },
  { label: 'Age Group', value: 'str_age_group', type: 'str' },
  { label: 'Gender', value: 'str_gender', type: 'str' },
  { label: 'Cell Number', value: 'int_cell_number', type: 'int' },
  { label: 'Race', value: 'str_race', type: 'str' },
  { label: 'Has Children', value: 'bol_has_children', type: 'bool' },
  { label: 'Is Married', value: 'bol_is_married', type: 'bool' },
  { label: 'Number of Children', value: 'int_number_of_children', type: 'int' },
  { label: 'Children Ages', value: 'obj_children_ages', type: 'str' },
  { label: 'Total Household Income', value: 'flt_total_household_income', type: 'float' },
  { label: 'City', value: 'str_city', type: 'str' },
  { label: 'Town', value: 'str_town', type: 'str' },
  { label: 'Region', value: 'str_region', type: 'str' },
  { label: 'Province', value: 'str_province', type: 'str' },
  { label: 'Country', value: 'str_country', type: 'str' },
  { label: 'Country Code', value: 'str_country_code', type: 'str' },
  { label: 'Instagram Handle', value: 'str_instagram', type: 'str' },
  { label: 'Twitter Handle', value: 'str_twitter', type: 'str' },
  { label: 'TikTok Handle', value: 'str_tiktok', type: 'str' },
];

// Operators for MongoDB queries
const OPERATOR_OPTIONS = {
  default: [
    { label: 'Contains', value: 'contains' },
    { label: 'Does Not Contain', value: 'not_contains' },
    { label: 'Equals', value: 'equals' },
    { label: 'Not Equals', value: 'not_equals' },
  ],
  numeric: [
    { label: 'Equals', value: 'equals' },
    { label: 'Not Equals', value: 'not_equals' },
    { label: 'Greater Than', value: 'gt' },
    { label: 'Less Than', value: 'lt' },
    { label: 'Greater or Equal', value: 'gte' },
    { label: 'Less or Equal', value: 'lte' },
  ],
  boolean: [
    { label: 'TRUE', value: 'equals_true' },
    { label: 'FALSE', value: 'equals_false' },
  ],
  gender: [
    { label: 'MALE', value: 'equals_male' },
    { label: 'FEMALE', value: 'equals_female' },
  ],
};

// MongoDB logical operators
const CONJUNCTION_OPTIONS = [
  { label: 'AND', value: 'AND' },
  { label: 'OR', value: 'OR' },
];

const convertValueByType = (value, fieldType) => {
  if (!value) return value;

  switch (fieldType) {
    case 'int':
      return parseInt(value, 10);
    case 'float':
      return parseFloat(value);
    case 'bool':
      return value.toLowerCase() === 'true';
    default:
      return value;
  }
};

const buildMongoQuery = (rules, conjunctions = []) => {
  if (rules.length === 0) return {};
  if (rules.length === 1) {
    const { field, operator, value } = rules[0];
    if (!value && !operator.includes('equals_')) return {};

    const result = {};
    // Find the field type from FIELD_OPTIONS
    const fieldOption = FIELD_OPTIONS.find((opt) => opt.value === field);
    const fieldType = fieldOption?.type || 'str';

    // Convert value based on field type and operator
    let convertedValue;
    if (fieldType === 'bool') {
      convertedValue = operator === 'equals_true';
    } else {
      convertedValue = convertValueByType(value, fieldType);
    }

    switch (operator) {
      case 'contains':
        result[field] = { $regex: value, $options: 'i' };
        break;
      case 'not_contains':
        result[field] = { $not: { $regex: value, $options: 'i' } };
        break;
      case 'equals_true':
        result[field] = true;
        break;
      case 'equals_false':
        result[field] = false;
        break;
      case 'equals':
        result[field] = convertedValue;
        break;
      case 'not_equals':
        result[field] = { $ne: convertedValue };
        break;
      case 'gt':
        result[field] = { $gt: convertedValue };
        break;
      case 'lt':
        result[field] = { $lt: convertedValue };
        break;
      case 'gte':
        result[field] = { $gte: convertedValue };
        break;
      case 'lte':
        result[field] = { $lte: convertedValue };
        break;
      default:
        result[field] = convertedValue;
    }
    return result;
  }

  // Helper function to create a condition object for a rule
  const createCondition = (rule) => {
    const { field, operator, value } = rule;
    if (!value && !operator.includes('equals_')) return null;

    // Find the field type from FIELD_OPTIONS
    const fieldOption = FIELD_OPTIONS.find((opt) => opt.value === field);
    const fieldType = fieldOption?.type || 'str';

    // Convert value based on field type and operator
    let convertedValue;
    if (fieldType === 'bool') {
      convertedValue = operator === 'equals_true';
    } else {
      convertedValue = convertValueByType(value, fieldType);
    }

    let condition = {};
    switch (operator) {
      case 'contains':
        condition[field] = { $regex: value, $options: 'i' };
        break;
      case 'equals_true':
        condition[field] = true;
        break;
      case 'equals_false':
        condition[field] = false;
        break;
      case 'equals':
        condition[field] = convertedValue;
        break;
      case 'not_equals':
        condition[field] = { $ne: convertedValue };
        break;
      case 'gt':
        condition[field] = { $gt: convertedValue };
        break;
      case 'lt':
        condition[field] = { $lt: convertedValue };
        break;
      case 'gte':
        condition[field] = { $gte: convertedValue };
        break;
      case 'lte':
        condition[field] = { $lte: convertedValue };
        break;
      default:
        condition[field] = convertedValue;
    }

    return condition;
  };

  // Process rules and build a properly nested query
  const validRules = rules.filter((rule) => rule.value || rule.operator?.includes('equals_'));
  if (validRules.length === 0) return {};
  if (validRules.length === 1) return createCondition(validRules[0]);

  // Special case for AND + OR pattern (most common use case)
  if (validRules.length === 3 && conjunctions[0] === 'AND' && conjunctions[1] === 'OR') {
    // Create the conditions
    const condition1 = createCondition(validRules[0]);
    const condition2 = createCondition(validRules[1]);
    const condition3 = createCondition(validRules[2]);

    if (condition1 && condition2 && condition3) {
      // Format: { $and: [ condition1, { $or: [condition2, condition3] } ] }
      return {
        $and: [condition1, { $or: [condition2, condition3] }],
      };
    }
  }

  // For all other cases, use the general approach
  // Identify segments of rules with the same conjunction
  const segments = [];
  let currentSegment = [];
  let currentConjunction = conjunctions[0] || 'AND';

  validRules.forEach((rule, index) => {
    const condition = createCondition(rule);
    if (!condition) return;

    // If this is the first rule or the conjunction is the same as the previous one
    if (
      currentSegment.length === 0 ||
      (index > 0 &&
        index - 1 < conjunctions.length &&
        conjunctions[index - 1] === currentConjunction)
    ) {
      currentSegment.push(condition);
    } else {
      // Save the current segment and start a new one
      if (currentSegment.length > 0) {
        segments.push({
          conjunction: currentConjunction,
          conditions: [...currentSegment],
        });
      }
      currentSegment = [condition];
      currentConjunction = index - 1 < conjunctions.length ? conjunctions[index - 1] : 'AND';
    }
  });

  // Add the last segment
  if (currentSegment.length > 0) {
    segments.push({
      conjunction: currentConjunction,
      conditions: currentSegment,
    });
  }

  // If we only have one segment, return it directly
  if (segments.length === 1) {
    const { conjunction, conditions } = segments[0];
    const operator = conjunction === 'AND' ? '$and' : '$or';
    const result = {};
    result[operator] = conditions;
    return result;
  }

  // For multiple segments, combine them with $and
  const result = { $and: [] };

  segments.forEach(({ conjunction, conditions }) => {
    if (conditions.length === 1) {
      // If there's only one condition in the segment, add it directly
      result.$and.push(conditions[0]);
    } else {
      // Otherwise, add the segment with its conjunction
      const operator = conjunction === 'AND' ? '$and' : '$or';
      const segmentQuery = {};
      segmentQuery[operator] = conditions;
      result.$and.push(segmentQuery);
    }
  });

  // If there's only one item in the $and array, simplify
  if (result.$and.length === 1) {
    return result.$and[0];
  }

  return result;
};

function RuleRow({ rule, index, updateRule, removeRule }) {
  // Handlers for field, operator and value changes
  const handleFieldChange = (val) => {
    updateRule(index, {
      ...rule,
      field: val,
      operator: '', // Reset operator when field changes
      value: '', // Reset value when field changes
    });
  };

  const handleOperatorChange = (val) => {
    const fieldOption = FIELD_OPTIONS.find((f) => f.value === rule.field);
    const isBooleanField = fieldOption?.type === 'bool';
    const isGenderField = fieldOption?.type === 'gender';

    updateRule(index, {
      ...rule,
      operator: val,
      // For boolean fields, set the value based on the operator
      value: isBooleanField
        ? val === 'equals_true'
          ? 'true'
          : 'false'
        : isGenderField
        ? val === 'equals_male'
          ? 'male'
          : 'female'
        : rule.value,
    });
  };

  const handleValueChange = (e) => {
    updateRule(index, { ...rule, value: e.target.value });
  };

  // Determine input type based on selected field
  const selectedFieldOption = FIELD_OPTIONS.find((f) => f.value === rule.field);
  const inputType = selectedFieldOption ? selectedFieldOption.type : 'text';
  const isBooleanField = inputType === 'bool';
  const isGenderField = inputType === 'gender';
  const isReadOnlyField = isBooleanField || isGenderField;

  // Get available operators based on field type
  const getAvailableOperators = () => {
    if (!selectedFieldOption) return OPERATOR_OPTIONS.default;
    if (selectedFieldOption.type === 'bool') return OPERATOR_OPTIONS.boolean;
    if (selectedFieldOption.type === 'gender') return OPERATOR_OPTIONS.gender;
    return ['int', 'float'].includes(selectedFieldOption.type)
      ? OPERATOR_OPTIONS.numeric
      : OPERATOR_OPTIONS.default;
  };

  return (
    <Row
      gutter={[12, 12]}
      align='middle'
      style={{
        marginBottom: 8,
      }}
    >
      <Col span={7}>
        <Select
          style={{
            width: '100%',
            height: '40px',
          }}
          value={rule.field}
          onChange={handleFieldChange}
          placeholder='Select Field'
          size='large'
        >
          {FIELD_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </Col>
      <Col span={7}>
        <Select
          style={{
            width: '100%',
            height: '40px',
          }}
          value={rule.operator}
          onChange={handleOperatorChange}
          placeholder='Select Operator'
          size='large'
        >
          {getAvailableOperators().map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </Col>
      <Col span={7}>
        <Input
          value={rule.value}
          onChange={handleValueChange}
          placeholder={isReadOnlyField ? 'Value set automatically' : 'Enter value'}
          type={inputType === 'int' || inputType === 'float' ? 'number' : 'text'}
          disabled={isReadOnlyField}
          readOnly={isReadOnlyField}
          style={{
            width: '100%',
            height: '40px',
            fontSize: '14px',
          }}
          size='large'
        />
      </Col>
      <Col span={3} style={{ textAlign: 'center', height: '40px', lineHeight: '40px' }}>
        <Tooltip placement='top' color={GlobalColors.mainPurple} title='Delete Rule'>
          <DeleteOutlined
            onClick={() => removeRule(index)}
            style={{
              color: GlobalColors.mainPurple,
              fontSize: 22,
              cursor: 'pointer',
            }}
          />
        </Tooltip>
      </Col>
    </Row>
  );
}

function ConjunctionSelector({ conjunction, onChange }) {
  return (
    <div className={styles.wrapper}>
      <div className={styles.dashedLine} />
      <Select
        value={conjunction}
        onChange={onChange}
        className={styles.select}
        size='small'
        placeholder='AND/OR'
      >
        {CONJUNCTION_OPTIONS.map((c) => (
          <Option key={c.value} value={c.value}>
            {c.label}
          </Option>
        ))}
      </Select>
    </div>
  );
}

const EmailSegmentationRules = ({ visible, onClose, initialValues, isEditing }) => {
  const axiosPrivate = usePrivateAxios();
  const dispatch = useDispatch();
  const [rules, setRules] = useState([]);
  const [conjunctions, setConjunctions] = useState([]);
  const [name, setName] = useState(initialValues?.name || '');
  const [description, setDescription] = useState(initialValues?.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingBatchSize, setIsLoadingBatchSize] = useState(false);
  const [lastAddedRuleIndex, setLastAddedRuleIndex] = useState(null);

  // Ref for rules container to enable auto-scrolling
  const rulesContainerRef = useRef(null);

  const { organizationId } = useSelector((state) => state.user);
  const { batchSize } = useSelector((state) => state.broadcastData);

  const handleCancel = () => {
    handleReset();
    onClose();
  };

  const fetchBatchSize = async (query) => {
    try {
      setIsLoadingBatchSize(true);
      const response = await axiosPrivate.post('/api/broadcasts/batch-size', {
        organizationId,
        options: {},
        segmentation: { properties: query },
      });
      dispatch({ type: 'FETCH_BATCH_SIZE', data: response.data || 0 });
    } catch (error) {
      console.error('Error fetching batch size:', error);
    } finally {
      setIsLoadingBatchSize(false);
    }
  };

  // Initialize form when editing
  useEffect(() => {
    if (isEditing && initialValues?.properties) {
      // Convert MongoDB query back to rules
      const query = initialValues.properties;
      let newRules = [];
      let newConjunctions = [];

      // Handle complex queries with $or/$and
      if (query.$or || query.$and) {
        const conditions = query.$or || query.$and;
        newConjunctions = Array(conditions.length - 1).fill(query.$or ? 'OR' : 'AND');
        
        newRules = conditions.map(condition => {
          const [field, value] = Object.entries(condition)[0];
          let operator = '';
          let fieldValue = '';

          if (typeof value === 'object') {
            if (value.$regex) {
              operator = 'contains';
              fieldValue = value.$regex;
            } else if (value.$ne) {
              operator = 'not_equals';
              fieldValue = value.$ne.toString();
            } else if (value.$gt) {
              operator = 'gt';
              fieldValue = value.$gt.toString();
            } else if (value.$lt) {
              operator = 'lt';
              fieldValue = value.$lt.toString();
            } else if (value.$gte) {
              operator = 'gte';
              fieldValue = value.$gte.toString();
            } else if (value.$lte) {
              operator = 'lte';
              fieldValue = value.$lte.toString();
            }
          } else {
            operator = value === true ? 'equals_true' : value === false ? 'equals_false' : 'equals';
            fieldValue = value.toString();
          }

          return { field, operator, value: fieldValue };
        });
      } else {
        // Handle simple queries
        newRules = Object.entries(query).map(([field, condition]) => {
          let operator = '';
          let value = '';

          if (typeof condition === 'object') {
            if (condition.$regex) {
              operator = 'contains';
              value = condition.$regex;
            } else if (condition.$ne) {
              operator = 'not_equals';
              value = condition.$ne.toString();
            } else if (condition.$gt) {
              operator = 'gt';
              value = condition.$gt.toString();
            } else if (condition.$lt) {
              operator = 'lt';
              value = condition.$lt.toString();
            } else if (condition.$gte) {
              operator = 'gte';
              value = condition.$gte.toString();
            } else if (condition.$lte) {
              operator = 'lte';
              value = condition.$lte.toString();
            }
          } else {
            operator = condition === true ? 'equals_true' : condition === false ? 'equals_false' : 'equals';
            value = condition.toString();
          }

          return { field, operator, value };
        });

        // Set default conjunctions for simple queries
        if (newRules.length > 1) {
          newConjunctions = Array(newRules.length - 1).fill('AND');
        }
      }

      setRules(newRules);
      setConjunctions(newConjunctions);
    } else {
      // Default rule for new segmentation
      setRules([
        {
          field: 'email',
          operator: 'contains',
          value: '@',
        },
      ]);
    }
  }, [isEditing, initialValues]);

  // Update batch size when rules change
  useEffect(() => {
    if (
      rules.some(
        (rule) =>
          rule.field &&
          rule.operator &&
          ((rule.value && rule.value.trim() !== '') || rule.operator.includes('equals_'))
      )
    ) {
      const mongodbQuery = buildMongoQuery(rules, conjunctions);
      fetchBatchSize(mongodbQuery);
    }
  }, [rules]);

  const addRule = () => {
    const newRules = [
      ...rules,
      {
        field: 'email',
        operator: 'contains',
        value: '',
      },
    ];
    setRules(newRules);
    setConjunctions([...conjunctions, 'AND']);
    // Set the last added rule index to highlight it
    setLastAddedRuleIndex(newRules.length - 1);

    // Clear the highlight after 2 seconds
    setTimeout(() => {
      setLastAddedRuleIndex(null);
    }, 2000);

    // Only fetch batch size if we have complete rules
    if (
      rules.some(
        (rule) =>
          rule.field &&
          rule.operator &&
          ((rule.value && rule.value.trim() !== '') || rule.operator.includes('equals_'))
      )
    ) {
      const mongodbQuery = buildMongoQuery(newRules);
      fetchBatchSize(mongodbQuery);
    }

    // Call scrollToBottom directly - the function itself has the necessary timeouts
    scrollToBottom();
  };

  // Function to scroll to the bottom of the rules container
  const scrollToBottom = () => {
    if (rulesContainerRef.current) {
      // Simply set scrollTop to scrollHeight for immediate scroll
      // This is the most reliable method
      setTimeout(() => {
        if (rulesContainerRef.current) {
          rulesContainerRef.current.scrollTop = rulesContainerRef.current.scrollHeight;
        }
      }, 50);

      // Then set it again after a slightly longer delay to ensure it catches any delayed renders
      setTimeout(() => {
        if (rulesContainerRef.current) {
          rulesContainerRef.current.scrollTop = rulesContainerRef.current.scrollHeight;
        }
      }, 150);
    }
  };

  const removeRule = (index) => {
    const newRules = rules.filter((_, i) => i !== index);
    const newConjunctions = conjunctions.filter((_, i) => i !== index - 1);
    setRules(newRules);
    setConjunctions(newConjunctions);
    const mongodbQuery = buildMongoQuery(newRules);
    fetchBatchSize(mongodbQuery);
  };

  const updateRule = (index, updatedRule) => {
    const newRules = [...rules];
    newRules[index] = updatedRule;
    setRules(newRules);
    const mongodbQuery = buildMongoQuery(newRules);
    fetchBatchSize(mongodbQuery);
  };

  const updateConjunction = (index, value) => {
    const newConjunctions = [...conjunctions];
    newConjunctions[index] = value;
    setConjunctions(newConjunctions);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      message.error('Please provide a name for the segmentation filter');
      return;
    }

    if (!isGenerateEnabled()) {
      message.error('Please add at least one valid condition');
      return;
    }

    try {
      setIsSaving(true);

      // Build MongoDB Query from rules
      const mongodbQuery = buildMongoQuery(rules, conjunctions);

      if (isEditing && initialValues) {
        const response = await axiosPrivate.put(`/api/segmentations/${initialValues.id}`, {
          name: name.trim(),
          description: description.trim(),
          properties: mongodbQuery,
        });

        if (response.data) {
          const updatedSegmentation = {
            id: initialValues.id,
            name: name.trim(),
            description: description.trim(),
            properties: mongodbQuery,
          };

          // Dispatch event with updated segmentation data
          window.dispatchEvent(
            new CustomEvent('segmentationUpdated', {
              detail: updatedSegmentation,
            })
          );

          dispatch({ type: 'UPDATE_SEGMENTATION', data: updatedSegmentation });
          dispatch({ type: 'SET_SELECTED_SEGMENTATION', data: updatedSegmentation });

          StatusNotification(
            'success',
            'Segmentation Updated',
            'Your segmentation filter has been updated successfully.'
          );
        }
      } else {
        const response = await axiosPrivate.post('/api/segmentations', {
          organizationId,
          name: name.trim(),
          description: description.trim(),
          properties: mongodbQuery,
        });

        if (response.data) {
          const newSegmentation = {
            id: response.data.segmentationId,
            name: name.trim(),
            description: description.trim(),
            properties: mongodbQuery,
          };

          // Dispatch event with new segmentation data
          window.dispatchEvent(
            new CustomEvent('segmentationUpdated', {
              detail: newSegmentation,
            })
          );

          dispatch({ type: 'ADD_SEGMENTATION', data: newSegmentation });
          dispatch({ type: 'SET_SELECTED_SEGMENTATION', data: newSegmentation });

          StatusNotification(
            'success',
            'Segmentation Created',
            'Your segmentation filter has been created successfully.'
          );
        }
      }

      handleReset();
      onClose();
    } catch (error) {
      console.error('Error saving segmentation:', error);
      // Extract error message from the response, fallback to generic message if not available
      const errorMessage =
        error.response?.data?.message ||
        'There was an error saving your segmentation filter. Please try again.';

      StatusNotification('error', 'Failed to Save Segmentation', errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setName('');
    setDescription('');
    fetchBatchSize({});
    setRules([
      {
        field: 'email',
        operator: 'contains',
        value: '@',
      },
    ]);
    setConjunctions([]);
  };

  const isGenerateEnabled = () => {
    return (
      name.trim() !== '' &&
      rules.some(
        (rule) =>
          (rule.value && rule.value.trim() !== '') ||
          (rule.operator && rule.operator.includes('equals_'))
      )
    );
  };

  const [showQuery, setShowQuery] = useState(false);
  const [copied, setCopied] = useState(false);
  const queryTextRef = useRef(null);
  const currentQuery = buildMongoQuery(rules, conjunctions);

  return (
    <Modal
      title='Segmentation Filter'
      open={visible}
      onCancel={handleCancel}
      footer={
        <>
          {rules.length > 0 && (
            <div className={styles.querySection}>
              <div className={styles.queryHeader}>
                <span style={{ color: GlobalColors.mainPurple, fontWeight: 500 }}>Query</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {showQuery && (
                    <Tooltip
                      placement='top'
                      color={GlobalColors.mainPurple}
                      title={copied ? 'Copied!' : 'Copy to clipboard'}
                    >
                      <Button
                        type='text'
                        icon={
                          copied ? (
                            <CheckOutlined />
                          ) : (
                            <CopyOutlined
                              style={{ color: GlobalColors.mainPurple, fontSize: 18 }}
                            />
                          )
                        }
                        style={{ marginRight: 8 }}
                        onClick={() => {
                          // Create a linearized version for copying
                          const linearQuery = JSON.stringify(formatQuery(currentQuery));
                          navigator.clipboard.writeText(linearQuery).then(() => {
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                          });
                        }}
                      />
                    </Tooltip>
                  )}
                  <Switch
                    checked={showQuery}
                    onChange={setShowQuery}
                    size='small'
                    style={{ backgroundColor: showQuery ? GlobalColors.darkPurple : undefined }}
                  />
                </div>
              </div>
              {showQuery && (
                <div className={styles.queryContent} ref={queryTextRef}>
                  <code style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
                    {JSON.stringify(formatQuery(currentQuery))}
                  </code>
                </div>
              )}
            </div>
          )}

          <Row style={{ width: '100%', paddingLeft: 4, paddingRight: 4 }}>
            <Col span={24}>
              <Row gutter={12} justify='start'>
                <Col flex={1}>
                  <Button
                    type='dashed'
                    onClick={addRule}
                    icon={<PlusOutlined />}
                    style={{ height: 40, width: 160 }}
                  >
                    Add Condition
                  </Button>
                </Col>
                <Col flex={1}>
                  <Button onClick={handleReset} style={{ height: 40, width: 160 }}>
                    Reset
                  </Button>
                </Col>
                <Col flex={1}>
                  <Button
                    type='primary'
                    loading={isSaving}
                    onClick={handleSubmit}
                    disabled={!isGenerateEnabled()}
                    style={{ height: 40, width: 160 }}
                  >
                    {isEditing ? 'Update' : 'Save'}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      }
      width={700}
      style={{
        top: 10,
        maxHeight: 'calc(80vh - 160px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card
              style={{ backgroundColor: GlobalColors.whitePurple }}
              extra={
                <>
                  <span style={{ fontSize: 14 }}>Matches </span>
                  <Tag color={GlobalColors.lightPurple}>
                    {isLoadingBatchSize ? <LoadingIcon /> : batchSize}
                  </Tag>
                </>
              }
            >
              <Input
                placeholder='Name'
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase().replaceAll(' ', '_'))}
                style={{
                  marginBottom: 12,
                  fontSize: 15,
                  padding: '8px 12px',
                  height: 40,
                }}
                maxLength={22}
                size='middle'
              />
              <Input.TextArea
                placeholder='Description'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{
                  marginBottom: 24,
                  fontSize: 14,
                  padding: '8px 12px',
                }}
                rows={4}
              />
              <div
                ref={rulesContainerRef}
                className={styles.rulesContainer}
                style={{
                  maxHeight: rules.length > 1 ? '120px' : 'none',
                  overflowY: rules.length > 1 ? 'auto' : 'visible',
                  paddingRight: '8px',
                  marginBottom: '8px',
                  scrollBehavior: 'smooth',
                }}
              >
                <AnimatePresence>
                  {rules.map((rule, idx) => (
                    <motion.div
                      key={idx}
                      initial={
                        idx === rules.length - 1 && rules.length > 1 ? { opacity: 0, y: 20 } : false
                      }
                      animate={{
                        opacity: 1,
                        y: 0,
                        backgroundColor:
                          lastAddedRuleIndex === idx
                            ? GlobalColors.darkPurple + '40'
                            : 'transparent',
                      }}
                      transition={{
                        duration: 0.3,
                        backgroundColor: { duration: 0.5, ease: 'easeInOut' },
                      }}
                      style={{
                        borderRadius: '4px',
                        padding: '4px',
                      }}
                    >
                      <RuleRow
                        rule={rule}
                        index={idx}
                        updateRule={updateRule}
                        removeRule={removeRule}
                      />
                      {idx < rules.length - 1 && (
                        <ConjunctionSelector
                          conjunction={conjunctions[idx]}
                          onChange={(value) => updateConjunction(idx, value)}
                        />
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                  height: '40px',
                }}
              >
                <span style={{ fontSize: 14 }}>Number of Rules </span>
                <Tag color={GlobalColors.lightPurple} style={{ marginLeft: '8px' }}>
                  {rules.length}
                </Tag>
                {rules.length > 2 && 'scroll down, to see all rules'}
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default EmailSegmentationRules;
